import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { ProductResultsContext } from './ProductResultsContext';
import {
  getClearAllWithSort,
  getAppliedRefinements,
  getSortNameValue,
  storeLifecycleEvent,
  formatToFriendlyDisplayCount
} from '../product-results-helpers';
import { getFirstNonEmptyObject } from '../utils/searchHelpers';
import { AppliedFiltersChips } from './AppliedFiltersChips/AppliedFiltersChips';

const ResultsApplied = ({
  onAppliedChange,
  onDisplay,
  onDisplayChange,
  viewPrimaryFilters,
  hideResultsCount,
  isGetItFastOnPlp,
  data: dataProp,
  isPlpSpeedEnabled
}) => {
  const { data: dataContext } = useContext(ProductResultsContext);
  const theme = useTheme(ResultsApplied);
  const { hideResultsApplied } = theme.props;
  const [cancelClick, setCancelClick] = useState(false);
  const data = getFirstNonEmptyObject(dataContext, dataProp);
  const { appliedDimensions = [], metadata, searchReport } = data?.searchModel || {};
  const {
    keyword = '', sortBy = '', sortOrder = '', totalProducts = 0
  } = searchReport || {};

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const desktopSort = useRef(getSortNameValue({
    key: (sortBy && sortBy.toLowerCase()) || '',
    order: (sortOrder && sortOrder.toLowerCase()) || '',
    keyword
  }).value);

  let appliedRefinements = getAppliedRefinements(appliedDimensions, isGetItFastOnPlp);

  const handleSortOrderChange = (sortorder) => {
    if (sortorder) {
      desktopSort.current = `${sortorder.output.sortby}:${sortorder.output.sortorder}`;
    }
  };

  const handleClearAllClick = () => {
    document.body.classList.add('sui-opacity-50');
    setTimeout(() => {
      let count = 0;
      let url = '';
      getClearAllWithSort({ metadata, searchReport }).split('?').forEach((key) => {
        if (count >= 0) {
          url += (count === 0 ? key : (count === 1 ? '?' : '&') + key);
        }
        count += 1;
      });
      const refinement = {
        url,
        clear: true
      };
      if (onAppliedChange) {
        onAppliedChange({ refinement, onDisplay: 'false' });
      }

      // trigger below event for setting DDE.filters as empty
      storeLifecycleEvent('product-results.change-filters-refinements-clear-all', refinement);
      document.body.classList.remove('sui-opacity-50');
    }, 0);
  };

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.on('sortorder.value', handleSortOrderChange);
  }, [desktopSort]);

  useEffect(() => {
    // Removal of filters : upon click of 'x', trigger below event
    if (cancelClick) {
      if (appliedDimensions && appliedDimensions.length > 0) {
        /* if onDisplay filters is selected and user removes other filters from chip list
            add onDisplay info into appliedDimensions  */
        let newAppliedDimensions = [...appliedDimensions];
        if (onDisplay === 'true') {
          newAppliedDimensions.push({
            label: 'Get It Fast',
            refinement: [{
              label: 'On Display',
              refinementLabel: 'On Display',
              refinementKey: ''
            }]
          });
        }
        storeLifecycleEvent('product-results.change-filters-refinements-shortings', newAppliedDimensions);
      } else {
        // applied and removed 1st filter
        handleClearAllClick();
      }
      setCancelClick(false);
    }
  }, [appliedDimensions, cancelClick]);

  if (!data) return null;

  const handleOnDisplayChange = () => {
    // when onDisplay filters is removed from chip list
    storeLifecycleEvent('filters-refinements-visualNav.click', {
      dimensionName: 'Get It Fast',
      label: 'On Display',
      refinementLabel: 'On Display',
      refinementKey: '',
      selected: true
    });
    onDisplayChange({ useStoreFilter: 'false', multiStoreIds: [] });
  };

  if (onDisplay === 'true') {
    appliedRefinements.push({
      dimensionName: 'Get It Fast',
      refinementLabel: 'On Display',
      refinementKey: ''
    });
  }

  const handleFiltersChipChange = (props) => {
    setCancelClick(true);
    onAppliedChange(props);
  };

  // Hide product results for exchange users
  if (hideResultsApplied) {
    return null;
  }

  const shouldRenderResultsCount = !hideResultsCount && (totalProducts || totalProducts === 0);
  const friendlyDisplayCount = formatToFriendlyDisplayCount(totalProducts);

  return (

    <div className="sui-flex sui-flex-col sui-w-full" data-component="ResultsApplied" data-testid="resultsApplied">
      <div className="sui-overflow-x-auto">
        {shouldRenderResultsCount && (
          <div className="sui-flex sui-flex-row sui-flex-wrap sui-gap-2">
            <Typography
              id="plp-total-results-count"
              data-plp-total-results-count={totalProducts}
              aria-label={`${friendlyDisplayCount} product results`}
              variant="body-lg"
              weight="bold"
              height="tight"
            >
              {`${friendlyDisplayCount} results`}
            </Typography>
          </div>
        )}
        {!viewPrimaryFilters && (
          <AppliedFiltersChips
            appliedRefinements={appliedRefinements}
            desktopSort={desktopSort.current}
            enableClearAll={appliedRefinements.length > 1}
            isMobile={isMobile}
            onClearAll={handleClearAllClick}
            onDisplay={onDisplay}
            onDisplayChange={handleOnDisplayChange}
            onFiltersChipChange={handleFiltersChipChange}
            isPlpSpeedEnabled={isPlpSpeedEnabled}
          />
        )}
      </div>
    </div>
  );
};

ResultsApplied.propTypes = {
  onAppliedChange: PropTypes.func.isRequired,
  onDisplay: PropTypes.string,
  onDisplayChange: PropTypes.func,
  viewPrimaryFilters: PropTypes.bool,
  hideResultsCount: PropTypes.bool,
  isGetItFastOnPlp: PropTypes.bool,
  data: PropTypes.shape({}),
  isPlpSpeedEnabled: PropTypes.bool
};

ResultsApplied.defaultProps = {
  onDisplay: 'false',
  onDisplayChange: null,
  viewPrimaryFilters: false,
  hideResultsCount: false,
  isGetItFastOnPlp: false,
  data: undefined,
  isPlpSpeedEnabled: false
};

ResultsApplied.defaultThemeProps = {
  hideResultsApplied: false
};

ResultsApplied.displayName = 'ResultsApplied';

export { ResultsApplied };
